import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Datetime from "react-datetime";
import { getPublicStoreSchedule } from "../../helpers/apiHelper";
import { buildSchedule } from "./components/scheduleUtils";
import ScheduleTable from "./components/ScheduleTable";
import RideModal from "./components/RideModal";
import QueryString from "query-string";
import "./DispatchOverview.css";
import "./StoreSchedulePublic.css";

const StoreSchedulePublic = ({ location }) => {
  const { t, i18n } = useTranslation();
  // moment's global locale is forced to 'fr' by `require('moment/locale/fr')` in
  // dateHelper, so the calendar ignores the app language. Drive it off i18n instead.
  const dateLocale = i18n.language && i18n.language.includes("fr") ? "fr" : "en";
  const currentLang = i18n.language && i18n.language.includes("fr") ? "fr" : "en";
  const token = QueryString.parse(location ? location.search : "").token || "";
  const [store, setStore] = useState(null);
  const [rides, setRides] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => moment().startOf("day"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [selectedRide, setSelectedRide] = useState(null);
  const isMountedRef = useRef(true);
  const ridesCacheRef = useRef({});

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchSchedule = useCallback(async () => {
    if (!token) { setError("Invalid or missing token."); setLoading(false); return; }
    const dateStr = selectedDate.format("YYYY-MM-DD");
    setRides(ridesCacheRef.current[dateStr] || []);
    try {
      const data = await getPublicStoreSchedule(token, dateStr);
      if (!isMountedRef.current) return;
      const fetched = data.rides || [];
      setStore(data.store);
      ridesCacheRef.current[dateStr] = fetched;
      setRides(fetched);
      setLastRefreshed(new Date());
    } catch (err) {
      if (isMountedRef.current) setError("Failed to load schedule. The link may have expired.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [token, selectedDate]);

  useEffect(() => {
    fetchSchedule();
    const interval = setInterval(fetchSchedule, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSchedule]);

  const schedule = buildSchedule(store, rides);

  return (
    <>
      <div className="store-schedule-public-page">
        <div className="store-schedule-public-card">
          <div className="store-schedule-public-topbar">
            <div className="store-schedule-lang-toggle">
              {["en", "fr"].map((lng) => (
                <button
                  key={lng}
                  type="button"
                  className={
                    "store-schedule-lang-btn" + (currentLang === lng ? " active" : "")
                  }
                  onClick={() => i18n.changeLanguage(lng)}
                >
                  {lng.toUpperCase()}
                </button>
              ))}
            </div>

            {lastRefreshed && (
              <p className="store-schedule-public-refreshed">
                {t("LAST_REFRESHED")}: {lastRefreshed.toLocaleTimeString()}
              </p>
            )}
          </div>

          <div className="store-schedule-public-header">
            <h4 className="title">{store ? store.name : "Schedule"}</h4>

            {!error && (
              <div className="dispatch-overview-date-wrapper">
                <Datetime
                  onChange={(val) => {
                    if (moment.isMoment(val)) setSelectedDate(val.startOf("day"));
                  }}
                  value={selectedDate}
                  dateFormat="YYYY/MM/DD"
                  timeFormat={false}
                  closeOnSelect
                  locale={dateLocale}
                  className="dispatch-overview-datetime"
                  inputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    className: "form-control dispatch-overview-date-input",
                  }}
                />
                <i className="fa fa-calendar dispatch-overview-date-icon" />
              </div>
            )}
          </div>

          {error && <p className="text-danger">{error}</p>}
          {loading && <p>{t("LOADING")}…</p>}

          {!loading && !error && (
            <ScheduleTable schedule={schedule} onRideClick={setSelectedRide} />
          )}
        </div>
      </div>

      <RideModal ride={selectedRide} onClose={() => setSelectedRide(null)} />
    </>
  );
};

export default StoreSchedulePublic;
